import styles from "./ToastContainer.module.scss";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import useToasts from "./useToasts.ts";
import { useTransitionMap } from "react-transition-state";
import { clsx } from "clsx";
import { useIntl } from "react-intl";
import { handOffToastFocus, toastHasFocus } from "./toastFocus.ts";

interface ExtraToastProps {
  onClose: () => void;
}

export default function ToastContainer() {
  const { toasts, closeToast } = useToasts();
  const listRef = useRef<HTMLUListElement>(null);
  const focusedToastIdRef = useRef<string>();
  const openToastIdsRef = useRef<string[]>([]);
  const openerRef = useRef<HTMLElement | null>(null);
  const openerScopeRef = useRef<HTMLElement | null>(null);
  const knownToastIdsRef = useRef<string[]>([]);
  const transitioner = useTransitionMap<string>({
    allowMultiple: true,
    timeout: 500,
    initialEntered: true,
    onStateChange: (event) => {
      if (event.current.status === "exited") {
        transitioner.deleteItem(event.key);
        closeToast(event.key);
      }
    },
  });

  useEffect(() => {
    toasts.forEach((toast) => {
      if (!transitioner.stateMap.get(toast.id)) {
        transitioner.setItem(toast.id);
      }
    });
  }, [transitioner, toasts]);

  // Runs after a newly mounted toast's own focus effect, so onFocus can tell an
  // automatic focus pull (a toast not yet seen) from a manual re-entry.
  useEffect(() => {
    knownToastIdsRef.current = toasts.map((toast) => toast.id);
  }, [toasts]);

  // a toast added this render has no transition state yet and counts as open
  const openToastIds = useMemo(
    () =>
      toasts
        .filter(
          (toast) => transitioner.stateMap.get(toast.id)?.isEnter !== false
        )
        .map((toast) => toast.id),
    [toasts, transitioner.stateMap]
  );

  useEffect(() => {
    // Most escape key handlers are on keydown. Allows access to the shared
    // preventDefault/defaultPrevented. React also batches unmounts until after
    // the dispatch, so higher-priority items usually stay mounted for the DOM
    // query too — defaultPrevented is the more reliable of the two signals.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || openToastIds.length === 0) {
        return;
      }

      // Priority 1: Close any focused toast
      const focusedToast = document.activeElement?.closest("[data-toast-id]");
      if (
        focusedToast instanceof HTMLElement &&
        listRef.current?.contains(focusedToast)
      ) {
        e.preventDefault();
        const focusedId = focusedToast.dataset.toastId!;
        handOffToastFocus(
          listRef.current,
          openToastIds,
          focusedId,
          openerRef.current,
          openerScopeRef.current
        );
        transitioner.toggle(focusedId, false);
        return;
      }
      // Fallback: Close all the toasts
      const hasHigherPriorityDismissableItem = document.querySelector(
        "[aria-modal='true'],[role='dialog'],[role='menu'],[role='listbox'],dialog"
      );
      // Heuristic for "yeah this should close". May have gaps. The main alternative
      // involves app-wide escape key orchestration and seems not necessary (yet)
      if (!e.defaultPrevented && !hasHigherPriorityDismissableItem) {
        e.preventDefault();
        openToastIds.forEach((id) => transitioner.toggle(id, false));
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openToastIds, transitioner]);

  /**
   * Covers toasts closed programmatically, which unmount without transitioning
   * and so never reach the injected onClose
   */
  useLayoutEffect(() => {
    const previousIds = openToastIdsRef.current;
    openToastIdsRef.current = openToastIds;
    const closedId = previousIds.find((id) => !openToastIds.includes(id));
    if (
      closedId &&
      closedId === focusedToastIdRef.current &&
      (!document.activeElement || document.activeElement === document.body)
    ) {
      focusedToastIdRef.current = undefined;
      handOffToastFocus(
        listRef.current,
        previousIds,
        closedId,
        openerRef.current,
        openerScopeRef.current
      );
    }
    if (openToastIds.length === 0) {
      openerRef.current = null;
      openerScopeRef.current = null;
    }
  }, [openToastIds]);

  const locale = useIntl().locale;

  return (
    // data-label is just to assist with finding it in the inspector
    // data-locale is a multi-language debugging convenience
    <ul
      data-label="toasts"
      className={styles.container}
      data-locale={locale}
      ref={listRef}
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => {
        const status = transitioner.stateMap.get(toast.id)?.status;
        return (
          <li
            className={clsx(
              styles.toastItem,
              (status === "entering" || status === "entered") &&
                styles.toastEnter
            )}
            key={toast.id}
            data-toast-id={toast.id}
            onFocus={(event) => {
              const cameFromOutside = !listRef.current?.contains(
                event.relatedTarget
              );
              const isNewToast = !knownToastIdsRef.current.includes(toast.id);
              if (cameFromOutside && isNewToast) {
                openerRef.current =
                  event.relatedTarget instanceof HTMLElement &&
                  event.relatedTarget !== document.body
                    ? event.relatedTarget
                    : null;
                openerScopeRef.current =
                  openerRef.current?.closest<HTMLElement>(
                    "[data-focus-scope]"
                  ) ?? null;
              }
              focusedToastIdRef.current = toast.id;
            }}
            onBlur={(event) => {
              if (!listRef.current?.contains(event.relatedTarget)) {
                focusedToastIdRef.current = undefined;
                openerRef.current = null;
                openerScopeRef.current = null;
              }
            }}
          >
            {React.cloneElement<ExtraToastProps>(toast.toastElement, {
              onClose: () => {
                if (toastHasFocus(listRef.current, toast.id)) {
                  handOffToastFocus(
                    listRef.current,
                    openToastIds,
                    toast.id,
                    openerRef.current,
                    openerScopeRef.current
                  );
                }
                transitioner.toggle(toast.id, false);
              },
            })}
          </li>
        );
      })}
    </ul>
  );
}
